import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  makeStyles,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  FormGroup,
  FormControlLabel,
  FormLabel,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import BusinessIcon from '@material-ui/icons/Business';
import FolderIcon from '@material-ui/icons/Folder';
import { Alert } from '@material-ui/lab';
import { useApi, googleAuthApiRef } from '@backstage/core-plugin-api';
import { homepageGcpProjectsApiRef } from '../../api';
import { GcpOrganization, OrgProject, CreateGcpProjectInput } from '../../types';

const useStyles = makeStyles(theme => ({
  authSection: {
    marginBottom: theme.spacing(2),
  },
  orgSelect: {
    marginBottom: theme.spacing(2),
    minWidth: 300,
  },
  searchField: {
    marginBottom: theme.spacing(2),
  },
  projectList: {
    maxHeight: 400,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  projectItem: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  projectInfo: {
    display: 'flex',
    flexDirection: 'column',
  },
  projectLabels: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  labelChip: {
    height: 20,
    fontSize: '0.7rem',
  },
  selectedCount: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  groupSection: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  emptyState: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  error: {
    marginTop: theme.spacing(2),
    color: theme.palette.error.main,
  },
}));

interface OrgProjectBrowserDialogProps {
  open: boolean;
  onClose: () => void;
  onAddProjects: (inputs: CreateGcpProjectInput[]) => Promise<void>;
  userGroups: string[];
  userRef?: string;
  existingProjectIds: string[];
}

export const OrgProjectBrowserDialog = ({
  open,
  onClose,
  onAddProjects,
  userGroups,
  userRef,
  existingProjectIds,
}: OrgProjectBrowserDialogProps) => {
  const classes = useStyles();
  const googleAuthApi = useApi(googleAuthApiRef);
  const gcpProjectsApi = useApi(homepageGcpProjectsApiRef);

  const [googleToken, setGoogleToken] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const [organizations, setOrganizations] = useState<GcpOrganization[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  const [projects, setProjects] = useState<OrgProject[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Try to get existing Google token when dialog opens
  useEffect(() => {
    if (!open) return;

    const checkAuth = async () => {
      try {
        const token = await googleAuthApi.getAccessToken(
          ['https://www.googleapis.com/auth/cloud-platform.read-only'],
          { optional: true },
        );
        if (token) {
          setGoogleToken(token);
          setNeedsAuth(false);
        }
      } catch {
        setNeedsAuth(true);
      }
    };

    checkAuth();
  }, [open, googleAuthApi]);

  // Load organizations when we have a token
  useEffect(() => {
    if (!googleToken || !open) return;

    const loadOrgs = async () => {
      setLoadingOrgs(true);
      setAuthError(null);
      try {
        const orgs = await gcpProjectsApi.getOrganizations(googleToken);
        setOrganizations(orgs);
        if (orgs.length === 1) {
          setSelectedOrg(orgs[0].name);
        }
      } catch (err: any) {
        console.error('Failed to load organizations:', err);
        if (err.message?.includes('401') || err.message?.includes('403')) {
          setNeedsAuth(true);
          setGoogleToken(null);
          setAuthError('Your Google token has expired. Please sign in again.');
        }
      } finally {
        setLoadingOrgs(false);
      }
    };

    loadOrgs();
  }, [googleToken, open, gcpProjectsApi]);

  // Load projects when org is selected
  useEffect(() => {
    if (!googleToken || !selectedOrg || !open) return;

    const loadProjects = async () => {
      setLoadingProjects(true);
      try {
        const orgProjects = await gcpProjectsApi.getOrgProjects(googleToken, selectedOrg);
        setProjects(orgProjects);
      } catch (err: any) {
        console.error('Failed to load projects:', err);
        if (err.message?.includes('401') || err.message?.includes('403')) {
          setNeedsAuth(true);
          setGoogleToken(null);
          setAuthError('Your Google token has expired. Please sign in again.');
        }
      } finally {
        setLoadingProjects(false);
      }
    };

    loadProjects();
  }, [googleToken, selectedOrg, open, gcpProjectsApi]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedOrg('');
      setProjects([]);
      setSearchQuery('');
      setSelectedProjects(new Set());
      setSelectedGroups([]);
      setError(null);
    }
  }, [open]);

  const handleGoogleSignIn = useCallback(async () => {
    try {
      setAuthError(null);
      const token = await googleAuthApi.getAccessToken([
        'https://www.googleapis.com/auth/cloud-platform.read-only',
      ]);
      if (token) {
        setGoogleToken(token);
        setNeedsAuth(false);
      }
    } catch (err: any) {
      console.error('Google sign-in failed:', err);
      setAuthError('Failed to sign in with Google. Please try again.');
    }
  }, [googleAuthApi]);

  const handleProjectToggle = useCallback((projectId: string) => {
    setSelectedProjects(prev => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  const handleGroupToggle = useCallback((groupRef: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupRef)
        ? prev.filter(g => g !== groupRef)
        : [...prev, groupRef],
    );
  }, []);

  // Filter projects by search query - compute before callbacks that use it
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        project.projectId.toLowerCase().includes(query) ||
        project.displayName?.toLowerCase().includes(query) ||
        Object.entries(project.labels || {}).some(
          ([k, v]) => k.toLowerCase().includes(query) || v.toLowerCase().includes(query),
        )
      );
    });
  }, [projects, searchQuery]);

  const handleSelectAll = useCallback(() => {
    const filteredIds = filteredProjects
      .filter(p => !existingProjectIds.includes(p.projectId))
      .map(p => p.projectId);
    setSelectedProjects(new Set(filteredIds));
  }, [filteredProjects, existingProjectIds]);

  const handleDeselectAll = useCallback(() => {
    setSelectedProjects(new Set());
  }, []);

  const handleSubmit = useCallback(async () => {
    if (selectedProjects.size === 0) {
      setError('Select at least one project');
      return;
    }
    if (selectedGroups.length === 0) {
      setError('Select at least one group to add projects to');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const inputs: CreateGcpProjectInput[] = Array.from(selectedProjects).map(projectId => {
        const project = projects.find(p => p.projectId === projectId);
        return {
          projectId,
          displayName: project?.displayName,
          groupRefs: selectedGroups,
        };
      });

      await onAddProjects(inputs);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add projects');
    } finally {
      setSubmitting(false);
    }
  }, [selectedProjects, selectedGroups, projects, onAddProjects, onClose]);

  const formatGroupName = (groupRef: string) => {
    const parts = groupRef.split('/');
    const name = parts[parts.length - 1];
    return name.charAt(0).toUpperCase() + name.slice(1).replace(/-/g, ' ');
  };

  const formatOrgName = (org: GcpOrganization) => {
    return org.displayName || org.name.replace('organizations/', 'Org ');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Browse Organization Projects</DialogTitle>
      <DialogContent>
        {needsAuth ? (
          <Box className={classes.authSection}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                Sign in with Google to browse GCP projects in your organization.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleGoogleSignIn}
                style={{ marginTop: 8 }}
              >
                Sign in with Google
              </Button>
            </Alert>
            {authError && (
              <Typography className={classes.error} variant="body2">
                {authError}
              </Typography>
            )}
          </Box>
        ) : (
          <>
            {loadingOrgs ? (
              <Box className={classes.loadingContainer}>
                <CircularProgress size={24} />
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                  Loading organizations...
                </Typography>
              </Box>
            ) : organizations.length === 0 ? (
              <Alert severity="warning">
                No organizations found. Make sure your Google account has access to at least one GCP organization.
              </Alert>
            ) : (
              <>
                <FormControl variant="outlined" fullWidth className={classes.orgSelect}>
                  <InputLabel>Organization</InputLabel>
                  <Select
                    value={selectedOrg}
                    onChange={e => setSelectedOrg(e.target.value as string)}
                    label="Organization"
                    startAdornment={<BusinessIcon style={{ marginRight: 8 }} />}
                  >
                    {organizations.map(org => (
                      <MenuItem key={org.name} value={org.name}>
                        {formatOrgName(org)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {selectedOrg && (
                  <>
                    <TextField
                      className={classes.searchField}
                      fullWidth
                      variant="outlined"
                      size="small"
                      placeholder="Search projects by name, ID, or labels..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                    />

                    {loadingProjects ? (
                      <Box className={classes.loadingContainer}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" style={{ marginLeft: 8 }}>
                          Loading projects...
                        </Typography>
                      </Box>
                    ) : filteredProjects.length === 0 ? (
                      <Box className={classes.emptyState}>
                        <FolderIcon style={{ fontSize: 48, opacity: 0.5 }} />
                        <Typography>
                          {searchQuery
                            ? 'No projects match your search'
                            : 'No projects found in this organization'}
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="body2" color="textSecondary">
                            {filteredProjects.length} projects found
                          </Typography>
                          <Box>
                            <Button size="small" onClick={handleSelectAll}>
                              Select All
                            </Button>
                            <Button size="small" onClick={handleDeselectAll}>
                              Deselect All
                            </Button>
                          </Box>
                        </Box>

                        <List className={classes.projectList} dense>
                          {filteredProjects.map(project => {
                            const isExisting = existingProjectIds.includes(project.projectId);
                            const isSelected = selectedProjects.has(project.projectId);

                            return (
                              <ListItem
                                key={project.projectId}
                                className={classes.projectItem}
                                button
                                onClick={() => !isExisting && handleProjectToggle(project.projectId)}
                                disabled={isExisting}
                              >
                                <ListItemIcon>
                                  <Checkbox
                                    checked={isSelected || isExisting}
                                    disabled={isExisting}
                                    color="primary"
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    <Box display="flex" alignItems="center">
                                      {project.displayName || project.projectId}
                                      {isExisting && (
                                        <Chip
                                          label="Already added"
                                          size="small"
                                          color="default"
                                          style={{ marginLeft: 8, height: 20 }}
                                        />
                                      )}
                                    </Box>
                                  }
                                  secondary={
                                    <Box className={classes.projectInfo}>
                                      <Typography variant="caption" color="textSecondary">
                                        {project.projectId}
                                      </Typography>
                                      {project.labels && Object.keys(project.labels).length > 0 && (
                                        <Box className={classes.projectLabels}>
                                          {Object.entries(project.labels).slice(0, 3).map(([k, v]) => (
                                            <Chip
                                              key={k}
                                              label={`${k}: ${v}`}
                                              size="small"
                                              variant="outlined"
                                              className={classes.labelChip}
                                            />
                                          ))}
                                          {Object.keys(project.labels).length > 3 && (
                                            <Chip
                                              label={`+${Object.keys(project.labels).length - 3}`}
                                              size="small"
                                              variant="outlined"
                                              className={classes.labelChip}
                                            />
                                          )}
                                        </Box>
                                      )}
                                    </Box>
                                  }
                                />
                                <ListItemSecondaryAction>
                                  {project.state && project.state !== 'ACTIVE' && (
                                    <Chip
                                      label={project.state}
                                      size="small"
                                      color="secondary"
                                    />
                                  )}
                                </ListItemSecondaryAction>
                              </ListItem>
                            );
                          })}
                        </List>
                      </>
                    )}

                    {selectedProjects.size > 0 && (
                      <Box className={classes.groupSection}>
                        <Typography className={classes.selectedCount} variant="body2">
                          <strong>{selectedProjects.size}</strong> project{selectedProjects.size !== 1 ? 's' : ''} selected
                        </Typography>

                        <FormControl component="fieldset">
                          <FormLabel component="legend">Add to:</FormLabel>
                          <FormGroup>
                            {userRef && (
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedGroups.includes(userRef)}
                                    onChange={() => handleGroupToggle(userRef)}
                                    color="primary"
                                  />
                                }
                                label="Myself (personal)"
                              />
                            )}
                            {userGroups.map(groupRef => (
                              <FormControlLabel
                                key={groupRef}
                                control={
                                  <Checkbox
                                    checked={selectedGroups.includes(groupRef)}
                                    onChange={() => handleGroupToggle(groupRef)}
                                    color="primary"
                                  />
                                }
                                label={formatGroupName(groupRef)}
                              />
                            ))}
                          </FormGroup>
                        </FormControl>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}

            {error && (
              <Typography className={classes.error} variant="body2">
                {error}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={submitting || selectedProjects.size === 0 || selectedGroups.length === 0 || needsAuth}
        >
          {submitting ? 'Adding...' : `Add ${selectedProjects.size} Project${selectedProjects.size !== 1 ? 's' : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
